/**
 * Voltage Theme - Product Form
 * Variant selection and add to cart for product pages
 */

class ProductForm {
  constructor(form) {
    this.form = form;
    this.productData = this.getProductData();

    if (!this.productData) {
      console.error('Product data not found');
      return;
    }

    this.variantInput = form.querySelector('[data-variant-id]');
    this.priceEl = document.querySelector('[data-price]');
    this.comparePriceEl = document.querySelector('[data-compare-price]');
    this.addToCartBtn = form.querySelector('[data-add-to-cart]');
    this.featuredImage = document.getElementById('product-featured-image');
    this.optionButtons = form.querySelectorAll('[data-option-value]');

    this.selectedOptions = [];
    this.currentVariant = null;

    this.init();
  }

  getProductData() {
    const jsonEl = document.querySelector('[data-product-json]');
    if (!jsonEl) return null;

    try {
      return JSON.parse(jsonEl.textContent);
    } catch (e) {
      console.error('Failed to parse product JSON:', e);
      return null;
    }
  }

  init() {
    // Initialize selected options from active buttons
    this.selectedOptions = this.productData.options.map((_, i) => {
      const activeBtn = this.form.querySelector(`[data-option-index="${i}"].product-form__variant--active`);
      return activeBtn ? activeBtn.dataset.optionValue : null;
    });

    // Set initial variant
    this.currentVariant = this.findVariant();

    // Option button clicks
    this.optionButtons.forEach(btn => {
      btn.addEventListener('click', () => this.handleOptionClick(btn));
    });

    // Quantity controls
    this.initQuantityControls();

    // Thumbnail gallery
    this.initThumbnails();
  }

  handleOptionClick(btn) {
    const optionIndex = parseInt(btn.dataset.optionIndex);
    const value = btn.dataset.optionValue;

    // Update selection UI
    const siblings = this.form.querySelectorAll(`[data-option-index="${optionIndex}"]`);
    siblings.forEach(b => b.classList.remove('product-form__variant--active'));
    btn.classList.add('product-form__variant--active');

    // Update selected options
    this.selectedOptions[optionIndex] = value;

    // Find matching variant
    this.currentVariant = this.findVariant();

    if (this.currentVariant) {
      this.updateVariant();
    }
  }

  findVariant() {
    return this.productData.variants.find(v => {
      return v.options.every((opt, i) => opt === this.selectedOptions[i]);
    });
  }

  updateVariant() {
    const variant = this.currentVariant;

    // Update hidden input
    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    // Update price
    if (this.priceEl) {
      this.priceEl.textContent = Voltage.formatMoney(variant.price);
    }

    // Update compare price
    if (this.comparePriceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.comparePriceEl.textContent = Voltage.formatMoney(variant.compare_at_price);
        this.comparePriceEl.style.display = '';
      } else {
        this.comparePriceEl.style.display = 'none';
      }
    }

    // Update add to cart button
    if (this.addToCartBtn) {
      if (variant.available) {
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.textContent = this.addToCartBtn.dataset.addText || 'ADD TO CART';
      } else {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.textContent = this.addToCartBtn.dataset.soldOutText || 'SOLD OUT';
      }
    }

    // Update featured image
    if (this.featuredImage && variant.featured_image) {
      const newSrc = variant.featured_image.src.replace(/\.([^.]+)$/, '_1000x.$1');
      this.featuredImage.src = newSrc;
    }

    // Update URL without page reload
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    // Publish variant change event
    Voltage.pubsub.publish('variant:changed', {
      product: this.productData,
      variant: variant
    });
  }

  initQuantityControls() {
    const quantityInput = this.form.querySelector('[data-quantity-input]');
    const minusBtn = this.form.querySelector('[data-quantity-minus]');
    const plusBtn = this.form.querySelector('[data-quantity-plus]');

    if (!quantityInput) return;

    if (minusBtn) {
      minusBtn.addEventListener('click', () => {
        const val = parseInt(quantityInput.value) || 1;
        if (val > 1) {
          quantityInput.value = val - 1;
        }
      });
    }

    if (plusBtn) {
      plusBtn.addEventListener('click', () => {
        const val = parseInt(quantityInput.value) || 1;
        quantityInput.value = val + 1;
      });
    }

    // Prevent negative values
    quantityInput.addEventListener('change', () => {
      const val = parseInt(quantityInput.value);
      if (isNaN(val) || val < 1) {
        quantityInput.value = 1;
      }
    });
  }

  initThumbnails() {
    const thumbnails = document.querySelectorAll('[data-thumbnail]');

    thumbnails.forEach(thumb => {
      thumb.addEventListener('click', () => {
        if (this.featuredImage) {
          this.featuredImage.src = thumb.dataset.imageUrl;
        }

        thumbnails.forEach(t => t.classList.remove('product-page__thumbnail--active'));
        thumb.classList.add('product-page__thumbnail--active');
      });
    });
  }
}

// Sticky add to cart for mobile
class StickyAddToCart {
  constructor() {
    this.stickyBar = document.getElementById('sticky-add-to-cart');
    this.mainAddBtn = document.querySelector('[data-add-to-cart]');
    this.stickyAddBtn = this.stickyBar?.querySelector('[data-sticky-add]');
    this.observer = null;
    this.unsubscribeVariant = null;

    if (!this.stickyBar || !this.mainAddBtn) return;

    this.init();
  }

  init() {
    // Show/hide based on main button visibility
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.stickyBar.classList.remove('is-visible');
        } else {
          this.stickyBar.classList.add('is-visible');
        }
      });
    }, {
      threshold: 0,
      rootMargin: '-100px 0px 0px 0px'
    });

    this.observer.observe(this.mainAddBtn);

    // Sync state with variant changes - store unsubscribe to prevent memory leak
    this.unsubscribeVariant = Voltage.pubsub.subscribe('variant:changed', ({ variant }) => {
      if (this.stickyAddBtn) {
        this.stickyAddBtn.disabled = !variant.available;

        const priceEl = this.stickyBar.querySelector('[data-sticky-price]');
        if (priceEl) {
          priceEl.textContent = Voltage.formatMoney(variant.price);
        }
      }
    });

    // Handle sticky add click
    if (this.stickyAddBtn) {
      this.stickyAddBtn.addEventListener('click', () => {
        this.mainAddBtn.click();
      });
    }
  }

  /**
   * Cleanup method to prevent memory leaks
   * Call this when the component is removed from the DOM
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.unsubscribeVariant) {
      this.unsubscribeVariant();
      this.unsubscribeVariant = null;
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', function() {
  const productForm = document.querySelector('[data-product-form]');
  if (productForm) {
    new ProductForm(productForm);
  }

  new StickyAddToCart();
});
